/**
 * SSR Data Fetching Helper
 *
 * This module provides utilities for fetching GraphQL data during SSR.
 * Use this instead of relying on getDataFromTree, which doesn't work
 * reliably with React 19.
 *
 * Usage in a page:
 *
 * ```js
 * import { fetchSSRData } from '@lib/ssr-fetch'
 *
 * MyPage.getInitialProps = async ({ query }) => {
 *   return fetchSSRData({
 *     query,
 *     graphqlQuery: MY_QUERY,
 *     variables: { slug: query.slug },
 *     clientName: 'coach-tools', // optional, defaults to Contentful
 *   })
 * }
 * ```
 */

import initApollo from './apollo-init'

function serializeGraphQLErrorList(errors = []) {
  return errors.map((item) => ({
    message: item?.message || 'Unknown GraphQL error',
    ...(item?.path ? { path: item.path } : {}),
    ...(item?.extensions?.code ? { code: item.extensions.code } : {}),
    ...(item?.code ? { code: item.code } : {}),
  }))
}

function serializeSSRFetchError(error) {
  if (!error) {
    return { name: 'Error', message: 'Unknown SSR fetch error' }
  }

  if (typeof error === 'string') {
    return { name: 'Error', message: error }
  }

  const serialized = {
    name: error.name || 'Error',
    message: error.message || 'Unknown SSR fetch error',
    ...(error.code ? { code: error.code } : {}),
  }

  if (Array.isArray(error.graphQLErrors) && error.graphQLErrors.length > 0) {
    serialized.graphQLErrors = error.graphQLErrors.map((graphQLError) => ({
      message: graphQLError.message,
      ...(graphQLError.path ? { path: graphQLError.path } : {}),
      ...(graphQLError.extensions?.code ? { code: graphQLError.extensions.code } : {}),
    }))
  }

  if (Array.isArray(error.errors) && error.errors.length > 0) {
    serialized.errors = serializeGraphQLErrorList(error.errors)
  }

  if (error.networkError) {
    serialized.networkError = {
      name: error.networkError.name || 'NetworkError',
      message: error.networkError.message || 'Unknown network error',
      ...(error.networkError.statusCode ? { statusCode: error.networkError.statusCode } : {}),
    }

    if (Array.isArray(error.networkError.result?.errors) && error.networkError.result.errors.length > 0) {
      serialized.networkError.resultErrors = serializeGraphQLErrorList(error.networkError.result.errors)
    }
  }

  return serialized
}

/**
 * Fetch GraphQL data during SSR
 *
 * @param {Object} options
 * @param {Object} options.query - The Next.js query object (will be returned in result)
 * @param {DocumentNode} options.graphqlQuery - The GraphQL query to execute
 * @param {Object} options.variables - Variables for the GraphQL query
 * @param {string} options.clientName - Optional Apollo client name (e.g., 'coach-tools', 'pga-algolia')
 * @param {Function} options.transform - Optional function to transform the data
 * @returns {Promise<Object>} Props to pass to the page component
 */
export async function fetchSSRData({
  query,
  graphqlQuery,
  variables,
  clientName,
  transform,
}) {
  const apollo = initApollo({ preview: query?.preview })

  try {
    const context = clientName ? { clientName } : undefined
    const response = await apollo.query({
      query: graphqlQuery,
      variables,
      context,
    })
    const { data, error, errors } = response

    if (error) throw error
    if (Array.isArray(errors) && errors.length > 0) {
      throw Object.assign(new Error('GraphQL query returned errors'), { errors })
    }

    const transformedData = transform ? transform(data) : data

    return {
      query,
      data: transformedData,
      ssrFetched: true,
    }
  } catch (error) {
    const serializedError = serializeSSRFetchError(error)

    // eslint-disable-next-line no-console
    console.error('[SSR] Error fetching data:', {
      error,
      serializedError,
      queryName: graphqlQuery?.definitions?.[0]?.name?.value,
      clientName,
    })
    return {
      query,
      error: serializedError.message,
      errorObject: serializedError,
      ssrFetched: true,
    }
  }
}

/**
 * Fetch multiple GraphQL queries during SSR
 *
 * @param {Object} options
 * @param {Object} options.query - The Next.js query object
 * @param {Array} options.queries - Array of query configs: { key, graphqlQuery, variables, clientName }
 * @returns {Promise<Object>} Props with data keyed by each query's key
 */
export async function fetchMultipleSSRData({ query, queries }) {
  const apollo = initApollo({ preview: query?.preview })

  const results = {}
  const errors = []

  await Promise.all(
    queries.map(async ({
      name, key, graphqlQuery, variables, clientName, transform, errorPolicy,
    }) => {
      const resultKey = name || key
      try {
        const context = clientName ? { clientName } : undefined
        const response = await apollo.query({
          query: graphqlQuery,
          variables,
          context,
          ...(errorPolicy && { errorPolicy }),
        })
        const { data, errors: graphQLErrors } = response

        if (Array.isArray(graphQLErrors) && graphQLErrors.length > 0) {
          // eslint-disable-next-line no-console
          console.error(`[SSR] Partial GraphQL errors for ${resultKey}:`, {
            errors: graphQLErrors,
            queryName: graphqlQuery?.definitions?.[0]?.name?.value,
            clientName,
          })
          errors.push({
            key: resultKey,
            name: 'GraphQLPartialError',
            message: graphQLErrors.map((item) => item?.message).filter(Boolean).join('; '),
            errors: serializeGraphQLErrorList(graphQLErrors),
          })
        }

        results[resultKey] = transform ? transform(data) : data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(`[SSR] Error fetching ${resultKey}:`, {
          error,
          queryName: graphqlQuery?.definitions?.[0]?.name?.value,
          clientName,
        })
        errors.push({ key: resultKey, message: error.message })
      }
    }),
  )

  return {
    query,
    ...results,
    ssrErrors: errors.length > 0 ? errors : undefined,
    ssrFetched: true,
  }
}

export default fetchSSRData
